/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies
import React from 'react';
import PropTypes from 'prop-types';

// Internal dependencies
import useLocateService from '@rakuten/hooks/useLocateService';
import useStyleSystem from '@rakuten/hooks/useStyleSystem';
import {
  Pressable,
  Text,
  View
} from '@rakuten/modules-core-components';
import Icon, {
  COLOR_VARIANT as ICON_COLOR_VARIANT,
  SIZE as ICON_SIZE
} from '../atoms/Icon';
import Spacer from './../atoms/Spacer';
import { isStringValidator, onPressValidator } from '@resources/extensions/propTypes';


// -----------------------------------------------------------------------------
// CONFIGURATION
// -----------------------------------------------------------------------------

/**
 * Available button types
 * @type {object}
 */
const TYPE = {
  FILL: 'fill',
  OUTLINE: 'outline',
  PLAIN: 'plain'
};

/**
 * Available button sizes
 * @type {object}
 */
const SIZE = {
  XSMALL: 'xsmall',
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  XLARGE: 'xlarge'
};

/**
 * Horizontal alignment of the icon besides the text, if text is available
 * @type {object}
 */
const ICON_ALIGN = {
  LEFT: 'left',
  RIGHT: 'right'
};

const TEXT_CASE = {
  AS_IS: 'asis',
  LOWER: 'lowercase',
  UPPER: 'uppercase'
}

// Mapping of button size identifiers to (label) font size identifiers
const MAP_FONT_SIZE = {
  [SIZE.XSMALL]: 'textSmall',
  [SIZE.SMALL]:  'caption',
  [SIZE.MEDIUM]: 'text',
  [SIZE.LARGE]:  'textLarge'
};


// -----------------------------------------------------------------------------
// AUXILIARY FUNCTIONS
// -----------------------------------------------------------------------------

/**
 * Returns the icon size identifier derived from the button @size.
 *
 * @param {String} size Button size identifier
 * @return {String} Icon size identifier
 */
function getIconSize(size) {

  switch(size){

    case SIZE.SMALL:
    case SIZE.XSMALL:
      return ICON_SIZE.SMALL;

    case SIZE.LARGE:
      return ICON_SIZE.LARGE;

    case SIZE.XLARGE:
      return ICON_SIZE.XLARGE;

    case SIZE.MEDIUM:
    default:
      return ICON_SIZE.DEFAULT;

  }
}


function getTransformedText(text, textCase) {
  if (!text || textCase === TEXT_CASE.AS_IS) {
    return text;
  }
  return textCase === TEXT_CASE.UPPER
    ? text.toUpperCase()
    : text.toLowerCase();
}


// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------

/**
 * Calculate styles for this component, optionally based on props, theme,
 * currently matching breakpoint etc.
 *
 * @param {object} args Arguments used for style calculation
 * @param {object} args.props Current props passed to the component
 * @param {object} args.theme Current theme object
 * @return {object} Object that maps style names to style objects
 */
function createStyles({ props, theme }) {
  const { colors, variables } = theme;
  const { size } = props;

  const stylePressed = {
    backgroundColor: colors.primaryPressed,
    cursor: 'pointer'
  };

  let height;
  let width;
  if (!props.text) {
    height = width = variables.button.iconButtonSize[size];
  } else {
    height = variables.button.size[size];
    width = props.fullWidth ? '100%' : null;
  }

  return {
    button: {
      alignItems: 'center',
      borderRadius: variables.button.borderRadius[size],
      display: 'flex',
      flexDirection: props.iconAlign === ICON_ALIGN.RIGHT ? 'row-reverse' : 'row',
      height,
      justifyContent: 'center',
      paddingLeft: props.text && variables.button.padding[size],
      paddingRight: props.text && variables.button.padding[size],
      maxWidth: '100%',
      width
    },

    nativeWrapper: {
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'flex-start'
    },

    text: theme.mixins.font(MAP_FONT_SIZE[size], 700),

    fill: {
      backgroundColor: colors.primary,
      ":active": stylePressed,
      ":hover:active": stylePressed,
      ":hover": {
        backgroundColor: colors.primaryPressed,
        cursor: 'pointer'
      }
    },
    fillText: {
      color: colors.background
    },
    fillDisabled: {
      backgroundColor: colors.primaryDisabled,
      cursor: 'inherit'
    },

    outline: {
      backgroundColor: colors.transparent,
      borderColor: colors.primary,
      borderStyle: 'solid',
      borderWidth: 1,
      ":active": stylePressed,
      ":hover:active": stylePressed,
      ":hover": {
        backgroundColor: colors.primaryHover,
        cursor: 'pointer'
      }
    },
    outlineText: {
      color: colors.primary
    },
    outlineTextDisabled: {
      color: colors.primaryDisabled
    },
    outlineDisabled: {
      borderColor: colors.primaryDisabled,
      backgroundColor: colors.transparent,
      cursor: 'inherit'
    },

    plain: {
      backgroundColor: colors.transparent,
      ":active": stylePressed,
      ":hover:active": stylePressed,
      ":hover": {
        backgroundColor: colors.primaryHover,
        cursor: 'pointer'
      }
    },
    plainText: {
      color: colors.primary
      // ":hover": {
      //   color: colors.white
      // }
    },
    plainTextDisabled: {
      color: colors.primaryDisabled
    }
  };
}


// -----------------------------------------------------------------------------
// BUTTON COMPONENT
// -----------------------------------------------------------------------------

/**
 * On native platforms, buttons are always as big as the enclosing View. To
 * simulate the `fit-content` behavior known from web browsers, the actual
 * button is encapsulated into a View that has some flex-box styling applied.
 *
 * @param {object} props React props
 * @return {React.Element} The actual component wrapped into a container view
 */
function NativeButtonWrapper(props) {
  return (
    <View style={ props.styles }>
      { props.children }
    </View>
  );
}


function Button(props) {
  const [ platform ] = useLocateService([ 'platform' ]);
  const { disabled, icon, testID, text, type } = props;

  // "Hook" this component into the theme/style system
  const [ styles ] = useStyleSystem(
    {
      id: 'Button',
      props,
      styleFn: createStyles
    },
    [
      props.iconAlign,
      props.size,
      text
    ]
  );


  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  // Get styles based on current props and component state
  const pressableStyles = [
    styles.button,
    type === TYPE[type.toUpperCase()] && styles[type],
    disabled && styles[`${type}Disabled`]
  ];

  const textStyles = [
    styles.text,
    type === TYPE[type.toUpperCase()] && styles[`${type}Text`],
    disabled && styles[`${type}TextDisabled`]
  ];

  // We need to calculate the color variant of the icon here and not in createStyles,
  // because we need the actual prop string to pass to the Icon component instead
  // of a (CSS) class name.
  const iconColorVariant = type === TYPE.FILL
    ? ICON_COLOR_VARIANT.BACKGROUND
    : ( disabled ? ICON_COLOR_VARIANT.PRIMARY_DISABLED : ICON_COLOR_VARIANT.PRIMARY );

  const accessibilityProps = {
    accessible: true,
    accessibilityLabel: props.accessibilityLabel,
    accessibilityRole: "button"
  };
  if (disabled) {
    accessibilityProps.accessibilityState = { disabled };
  }

  const Component = (
    <Pressable
      disabled={ disabled }
      onPress={ !disabled ? props.onPress : null }
      onKeyPress={ !disabled
        ? props.onKeyPress ? props.onKeyPress : props.onPress
        : null }
      style={ pressableStyles }
      testID={ testID }
      { ...accessibilityProps }
    >
      {
        icon &&
          <Icon
            accessibilityRole="none"
            accessible= { false }
            colorVariant={ iconColorVariant }
            icon={ icon }
            size={ getIconSize(props.size) }
            testID={ testID ? `${testID}-icon` : null }
          />
      }
      {
        icon && text &&
          <Spacer width={ .8 } />
      }
      {
        text &&
          <Text
          numberOfLines={ 1 }
          style={ textStyles }
          testID={ testID ? `${testID}-text` : null }
        >
          {
            getTransformedText(text, props.textCase)
          }
        </Text>
      }
    </Pressable>
  );

  if (platform) {
    if (platform.isWeb() === true) {
      return Component;
    }

    return (
      <NativeButtonWrapper styles={ styles.nativeWrapper }>
        { Component }
      </NativeButtonWrapper>
    );
  }

  return null;
}


// -----------------------------------------------------------------------------
// PROPS VALIDATION
// -----------------------------------------------------------------------------

Button.propTypes = {
  /** The text for a11y systems, required if prop `text` is _not_ defined */
  accessibilityLabel: PropTypes.oneOfType([ isStringValidator, onPressValidator ]),

  /** Whether or not the button is disabled [optional] */
  disabled: PropTypes.bool,

  /** Whether or not the button's width should be 100% of it's container [optional] */
  fullWidth: PropTypes.bool,

  /** An optional icon to display either besides the text or without any text [optional] */
  icon: PropTypes.string,

  /** The alignment of the icon besides any optional text [optional] */
  iconAlign: PropTypes.oneOf(Object.values(ICON_ALIGN)),

  /** A function to execute when the button is clicked/tapped */
  onPress: PropTypes.func.isRequired,

  /** The size of the button [optional] */
  size: PropTypes.oneOf(Object.values(SIZE)),

  /** ID for test automation [optional] */
  testID: PropTypes.string,

  /** The text to show as the button's label [optional] */
  text: PropTypes.string,

  textCase: PropTypes.oneOf(Object.values(TEXT_CASE)),

  /** The type of the button [optional] */
  type: PropTypes.oneOf(Object.values(TYPE))
};

Button.defaultProps = {
  fullWidth: false,
  iconAlign: ICON_ALIGN.LEFT,
  size: SIZE.LARGE,
  type: TYPE.FILL,
  textCase: TEXT_CASE.UPPER
};


// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export default Button;
export {
  ICON_ALIGN,
  SIZE,
  TEXT_CASE,
  TYPE
};
